import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnInit, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { ApplicationApi, Application } from '@venecia/apis/identity';
import { MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-application-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './application-grid.html',
  styleUrl: './application-grid.scss',
})
export class ApplicationGrid implements OnInit, OnChanges {
  private readonly applicationApi = inject(ApplicationApi);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly reloadVersion = input<number>(0);

  readonly detailRequested = output<Application>();

  readonly applications = signal<readonly Application[]>([]);
  readonly loading = signal(false);

  readonly pageNumber = signal(1); // 1-based
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);

  readonly trackByApplication = (_: number, a: Application) => a.id ?? a.key;

  readonly columns = computed<readonly Column<Application>[]>(() => [
    {
      id: 'logo-url',
      header: '',
      value: (a) => a['logo-url'],
      width: 10,
      isImage: true
    },
    {
      id: 'name',
      header: this.translateService.instant('pages.applications.fields.name'),
      value: (a) => a.name,
      width: 30
    },
    {
      id: 'site-url',
      header: this.translateService.instant('pages.applications.fields.site-url'),
      value: (a) => a['site-url'],
      width: 50
    },
  ]);

  readonly actions = computed<readonly RowAction<Application>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.applications.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadApplications();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    // Reset to first page on reload
    this.pageNumber.set(1);
    void this.loadApplications();
  }

  onActionSelected(ev: { actionId: string; item: Application }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }
    this.pageNumber.set(page);
    void this.loadApplications();
  }

  private async loadApplications(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.applicationApi.search({
          payload: {
            page: this.pageNumber() - 1,
            size: this.pageSize(),
          },
        })
      );

      this.applications.set(response.body.data ?? []);

      this.totalItems.set(
        response.body.total ?? 0
      );
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.translate('pages.applications.messages.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }
}
